import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../Style/HideCourses.css";
import judicialLogo from "../Images/judicialLogo.png";
import { useDispatch, useSelector } from "react-redux";
import { hideCourses, getAllStudents } from "../Redux/Dashboard/DashboardSlice";
import { getCoursesDetail } from "../Redux/Auth/AuthSlice";
import Spinner from "../Spinner/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { Notify } from "../CommonPopup/DashboardPopUp";

const HideCourses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selector = useSelector((s) => s.AuthSlice);
  const getCoursesDetails = selector.getCoursesDetails || [];
  
  const loader = selector.loader;

  // Get student from location state
  const student = location.state?.student;

  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedLessons, setExpandedLessons] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [lessonPages, setLessonPages] = useState({});
  const [subjectPages, setSubjectPages] = useState({});
  const [videoPages, setVideoPages] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [checkedCourses, setCheckedCourses] = useState({});
  const [checkedLessons, setCheckedLessons] = useState({});
  const [checkedSubjects, setCheckedSubjects] = useState({});
  const [checkedVideos, setCheckedVideos] = useState({});
  const [hiddenCourses, setHiddenCourses] = useState([]);
  const [hiddenLessons, setHiddenLessons] = useState([]);
  const [hiddenSubjects, setHiddenSubjects] = useState([]);
  const [hiddenContents, setHiddenContents] = useState([]);
  const [checkboxLoading, setCheckboxLoading] = useState({}); // Object for per-checkbox loading

  useEffect(() => {
    dispatch(getCoursesDetail());
  }, [dispatch]);

  // Initialize checkbox states based on student's hidden items
  useEffect(() => {
    const newCheckedCourses = {};
    const newCheckedLessons = {};
    const newCheckedSubjects = {};
    const newCheckedVideos = {};

    const studentHiddenCourses = student?.hidden_courses || [];
    const studentHiddenLessons = student?.hidden_lessons || [];
    const studentHiddenSubjects = student?.hidden_subjects || [];
    const studentHiddenContents = student?.hidden_contents || [];

    // Only process paid courses
    const paidCourses = getCoursesDetails.filter((course) => course.is_paid === true);
    paidCourses.forEach((course) => {
      newCheckedCourses[course.courseId] = !studentHiddenCourses.includes(course.courseId);
      course.lessons.forEach((lesson) => {
        newCheckedLessons[lesson.lessonId] = !studentHiddenLessons.includes(lesson.lessonId);
        lesson.subjects.forEach((subject) => {
          newCheckedSubjects[subject.subjectId] = !studentHiddenSubjects.includes(subject.subjectId);
          subject.videos.forEach((video) => {
            newCheckedVideos[video._id] = !studentHiddenContents.includes(video._id);
          });
        });
      });
    });

    setCheckedCourses(newCheckedCourses);
    setCheckedLessons(newCheckedLessons);
    setCheckedSubjects(newCheckedSubjects);
    setCheckedVideos(newCheckedVideos);
    setHiddenCourses(studentHiddenCourses);
    setHiddenLessons(studentHiddenLessons);
    setHiddenSubjects(studentHiddenSubjects);
    setHiddenContents(studentHiddenContents);
  }, [getCoursesDetails, student]);

  // Filter courses based on search query, dropdown selections, and is_paid
  useEffect(() => {
    // Start with only paid courses
    let filtered = getCoursesDetails.filter((course) => course.is_paid === true);

    if (searchQuery.trim()) {
      filtered = filtered
        .map((course) => {
          const filteredLessons = course.lessons
            .map((lesson) => {
              const filteredSubjects = lesson.subjects
                .map((subject) => {
                  const filteredVideos = subject.videos.filter(
                    (video) =>
                      checkedVideos[video._id] &&
                      video.name.toLowerCase().includes(searchQuery.toLowerCase())
                  );
                  return filteredVideos.length > 0 && checkedSubjects[subject.subjectId]
                    ? { ...subject, videos: filteredVideos }
                    : null;
                })
                .filter((subject) => subject !== null);
              return filteredSubjects.length > 0 && checkedLessons[lesson.lessonId]
                ? { ...lesson, subjects: filteredSubjects }
                : null;
            })
            .filter((lesson) => lesson !== null);
          return filteredLessons.length > 0 && checkedCourses[course.courseId]
            ? { ...course, lessons: filteredLessons }
            : null;
        })
        .filter((course) => course !== null);
    }

    if (selectedCourse) {
      filtered = filtered.filter((course) => course.courseId === selectedCourse.courseId);
      if (selectedLesson) {
        filtered = filtered.map((course) => ({
          ...course,
          lessons: course.lessons.filter((lesson) => lesson.lessonId === selectedLesson.lessonId),
        }));
        if (selectedSubject) {
          filtered = filtered.map((course) => ({
            ...course,
            lessons: course.lessons.map((lesson) => ({
              ...lesson,
              subjects: lesson.subjects.filter(
                (subject) => subject.subjectId === selectedSubject.subjectId
              ),
            })),
          }));
        }
      }
    }

    setFilteredCourses(filtered.length > 0 ? filtered : []);
  }, [searchQuery, getCoursesDetails, selectedCourse, selectedLesson, selectedSubject, checkedCourses, checkedLessons, checkedSubjects, checkedVideos]);

  // Handle expansions based on checkbox states and search/dropdown
  useEffect(() => {
    const newExpandedCourses = {};
    const newExpandedLessons = {};
    const newExpandedSubjects = {};

    if (searchQuery.trim() || selectedCourse) {
      filteredCourses.forEach((course) => {
        if (checkedCourses[course.courseId]) {
          newExpandedCourses[course.courseId] = true;
          course.lessons.forEach((lesson) => {
            if (checkedLessons[lesson.lessonId]) {
              newExpandedLessons[lesson.lessonId] = true;
              lesson.subjects.forEach((subject) => {
                if (checkedSubjects[subject.subjectId]) {
                  newExpandedSubjects[subject.subjectId] = true;
                }
              });
            }
          });
        }
      });
    }

    setExpandedCourses((prev) => ({ ...prev, ...newExpandedCourses }));
    setExpandedLessons((prev) => ({ ...prev, ...newExpandedLessons }));
    setExpandedSubjects((prev) => ({ ...prev, ...newExpandedSubjects }));
  }, [filteredCourses, checkedCourses, checkedLessons, checkedSubjects, searchQuery, selectedCourse]);

  // Calculate video counts
  const getSubjectVideoCounts = (subject) => {
    const totalVideos = subject.videos.length;
    const shownVideos = subject.videos.filter((video) => checkedVideos[video._id]).length;
    return { shownVideos, totalVideos };
  };

  const getLessonVideoCounts = (lesson) => {
    let totalVideos = 0;
    let shownVideos = 0;
    lesson.subjects.forEach((subject) => {
      const { shownVideos: subjectShown, totalVideos: subjectTotal } = getSubjectVideoCounts(subject);
      shownVideos += subjectShown;
      totalVideos += subjectTotal;
    });
    return { shownVideos, totalVideos };
  };

  const getCourseVideoCounts = (course) => {
    let totalVideos = 0;
    let shownVideos = 0;
    course.lessons.forEach((lesson) => {
      const { shownVideos: lessonShown, totalVideos: lessonTotal } = getLessonVideoCounts(lesson);
      shownVideos += lessonShown;
      totalVideos += lessonTotal;
    });
    return { shownVideos, totalVideos };
  };

  // Update parent checkboxes when a child is checked
  const updateParentCheckboxes = (type, id) => {
    if (type === "video") {
      const course = getCoursesDetails.find((c) =>
        c.lessons.some((l) => l.subjects.some((s) => s.videos.some((v) => v._id === id)))
      );
      const lesson = course?.lessons.find((l) =>
        l.subjects.some((s) => s.videos.some((v) => v._id === id))
      );
      const subject = lesson?.subjects.find((s) => s.videos.some((v) => v._id === id));
      if (subject && checkedVideos[id]) {
        setCheckedSubjects((prev) => ({ ...prev, [subject.subjectId]: true }));
        setHiddenSubjects((prev) => prev.filter((sid) => sid !== subject.subjectId));
      }
      if (lesson && checkedVideos[id]) {
        setCheckedLessons((prev) => ({ ...prev, [lesson.lessonId]: true }));
        setHiddenLessons((prev) => prev.filter((lid) => lid !== lesson.lessonId));
      }
      if (course && checkedVideos[id]) {
        setCheckedCourses((prev) => ({ ...prev, [course.courseId]: true }));
        setHiddenCourses((prev) => prev.filter((cid) => cid !== course.courseId));
      }
    } else if (type === "subject") {
      const course = getCoursesDetails.find((c) =>
        c.lessons.some((l) => l.subjects.some((s) => s.subjectId === id))
      );
      const lesson = course?.lessons.find((l) => l.subjects.some((s) => s.subjectId === id));
      if (lesson && checkedSubjects[id]) {
        setCheckedLessons((prev) => ({ ...prev, [lesson.lessonId]: true }));
        setHiddenLessons((prev) => prev.filter((lid) => lid !== lesson.lessonId));
      }
      if (course && checkedSubjects[id]) {
        setCheckedCourses((prev) => ({ ...prev, [course.courseId]: true }));
        setHiddenCourses((prev) => prev.filter((cid) => cid !== course.courseId));
      }
    } else if (type === "lesson") {
      const course = getCoursesDetails.find((c) => c.lessons.some((l) => l.lessonId === id));
      if (course && checkedLessons[id]) {
        setCheckedCourses((prev) => ({ ...prev, [course.courseId]: true }));
        setHiddenCourses((prev) => prev.filter((cid) => cid !== course.courseId));
      }
    }
  };

  // Handle checkbox changes with loading state
  const handleCheckboxChange = (type, id) => {
    setCheckboxLoading((prev) => ({ ...prev, [id]: true })); // Set loading for this checkbox
    if (type === "course") {
      setCheckedCourses((prev) => {
        const newChecked = { ...prev, [id]: !prev[id] };
        setHiddenCourses((prev) =>
          newChecked[id]
            ? prev.filter((courseId) => courseId !== id)
            : [...prev, id].filter((courseId, index, self) => self.indexOf(courseId) === index)
        );
        if (!newChecked[id] && selectedCourse?.courseId === id) {
          setSelectedCourse(null);
          setSelectedLesson(null);
          setSelectedSubject(null);
        }
        if (!newChecked[id]) {
          setExpandedCourses((prev) => ({ ...prev, [id]: false }));
        }
        Promise.resolve().then(() =>
          setCheckboxLoading((prev) => ({ ...prev, [id]: false }))
        ); // Clear loading
        return newChecked;
      });
    } else if (type === "lesson") {
      setCheckedLessons((prev) => {
        const newChecked = { ...prev, [id]: !prev[id] };
        setHiddenLessons((prev) =>
          newChecked[id]
            ? prev.filter((lessonId) => lessonId !== id)
            : [...prev, id].filter((lessonId, index, self) => self.indexOf(lessonId) === index)
        );
        if (!newChecked[id] && selectedLesson?.lessonId === id) {
          setSelectedLesson(null);
          setSelectedSubject(null);
        }
        if (!newChecked[id]) {
          setExpandedLessons((prev) => ({ ...prev, [id]: false }));
        }
        if (newChecked[id]) {
          updateParentCheckboxes(type, id);
        }
        Promise.resolve().then(() =>
          setCheckboxLoading((prev) => ({ ...prev, [id]: false }))
        ); // Clear loading
        return newChecked;
      });
    } else if (type === "subject") {
      setCheckedSubjects((prev) => {
        const newChecked = { ...prev, [id]: !prev[id] };
        setHiddenSubjects((prev) =>
          newChecked[id]
            ? prev.filter((subjectId) => subjectId !== id)
            : [...prev, id].filter((subjectId, index, self) => self.indexOf(subjectId) === index)
        );
        if (!newChecked[id] && selectedSubject?.subjectId === id) {
          setSelectedSubject(null);
        }
        if (!newChecked[id]) {
          setExpandedSubjects((prev) => ({ ...prev, [id]: false }));
        }
        if (newChecked[id]) {
          updateParentCheckboxes(type, id);
        }
        Promise.resolve().then(() =>
          setCheckboxLoading((prev) => ({ ...prev, [id]: false }))
        ); // Clear loading
        return newChecked;
      });
    } else if (type === "video") {
      setCheckedVideos((prev) => {
        const newChecked = { ...prev, [id]: !prev[id] };
        setHiddenContents((prev) =>
          newChecked[id]
            ? prev.filter((videoId) => videoId !== id)
            : [...prev, id].filter((videoId, index, self) => self.indexOf(videoId) === index)
        );
        if (newChecked[id]) {
          updateParentCheckboxes(type, id);
        }
        Promise.resolve().then(() =>
          setCheckboxLoading((prev) => ({ ...prev, [id]: false }))
        ); // Clear loading
        return newChecked;
      });
    }
  };

  // Check if there are changes to enable Save button
  const hasChanges = () => {
    const studentHiddenCourses = student?.hidden_courses || [];
    const studentHiddenLessons = student?.hidden_lessons || [];
    const studentHiddenSubjects = student?.hidden_subjects || [];
    const studentHiddenContents = student?.hidden_contents || [];

    return (
      JSON.stringify(hiddenCourses.sort()) !== JSON.stringify(studentHiddenCourses.sort()) ||
      JSON.stringify(hiddenLessons.sort()) !== JSON.stringify(studentHiddenLessons.sort()) ||
      JSON.stringify(hiddenSubjects.sort()) !== JSON.stringify(studentHiddenSubjects.sort()) ||
      JSON.stringify(hiddenContents.sort()) !== JSON.stringify(studentHiddenContents.sort())
    );
  };

  // Handle save courses
  const handleSaveCourses = () => {
    if (!student?._id) {
      Notify({
        data: "error",
        msg: "Student ID not found. Please select a student.",
        type: "error",
      });
      return;
    }

    dispatch(
      hideCourses({
        userId: student._id,
        courses: hiddenCourses,
        lessons: hiddenLessons,
        subjects: hiddenSubjects,
        contents: hiddenContents,
      })
    )
      .unwrap()
      .then((data) => {
        Notify({
          data: "success",
          msg: data?.message || "Hidden courses updated successfully",
          type: "success",
        });
        dispatch(getAllStudents());
        navigate(-1);
      })
      .catch((err) => {
        Notify({
          data: "error",
          msg: err.message || "Failed to update hidden courses",
          type: "error",
        });
        navigate(-1);
      });
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  // Toggle expand/collapse with checkbox check
  const toggleExpand = (type, id) => {
    if (type === "course" && !checkedCourses[id]) return;
    if (type === "lesson" && !checkedLessons[id]) return;
    if (type === "subject" && !checkedSubjects[id]) return;

    if (type === "course") {
      setExpandedCourses((prev) => ({ ...prev, [id]: !prev[id] }));
    } else if (type === "lesson") {
      setExpandedLessons((prev) => ({ ...prev, [id]: !prev[id] }));
    } else if (type === "subject") {
      setExpandedSubjects((prev) => ({ ...prev, [id]: !prev[id] }));
    }
  };

  // Handle pagination
  const handleSubPagination = (type, id, direction) => {
    if (type === "lesson") {
      setLessonPages((prev) => ({
        ...prev,
        [id]: (prev[id] || 1) + (direction === "next" ? 1 : -1),
      }));
    } else if (type === "subject") {
      setSubjectPages((prev) => ({
        ...prev,
        [id]: (prev[id] || 1) + (direction === "next" ? 1 : -1),
      }));
    } else if (type === "video") {
      setVideoPages((prev) => ({
        ...prev,
        [id]: (prev[id] || 1) + (direction === "next" ? 1 : -1),
      }));
    }
  };

  // Handle dropdown selection
  const handleDropdownChange = (type, value) => {
    if (type === "course") {
      const course = getCoursesDetails.find((c) => c.courseId === value && c.is_paid === true) || null;
      setSelectedCourse(course);
      setSelectedLesson(null);
      setSelectedSubject(null);
    } else if (type === "lesson") {
      const lesson = selectedCourse?.lessons.find((l) => l.lessonId === value) || null;
      setSelectedLesson(lesson);
      setSelectedSubject(null);
    } else if (type === "subject") {
      const subject = selectedLesson?.subjects.find((s) => s.subjectId === value) || null;
      setSelectedSubject(subject);
    }
  };

  // Render paginated items
  const renderPaginatedItems = (items, type, parentId, level, paginationStates) => {
    const subItemsPerPage = 5;
    let paginatedItems = items;
    let totalPages = 1;
    let currentPageForType = 1;

    if (type !== "course") {
      currentPageForType =
        type === "lesson"
          ? paginationStates.lessonPages[parentId] || 1
          : type === "subject"
            ? paginationStates.subjectPages[parentId] || 1
            : paginationStates.videoPages[parentId] || 1;
      const totalItems = items.length;
      totalPages = Math.ceil(totalItems / subItemsPerPage);
      const startIndex = (currentPageForType - 1) * subItemsPerPage;
      const endIndex = startIndex + subItemsPerPage;
      paginatedItems = items.slice(startIndex, endIndex);
    }

    return (
      <div className={`hcv-tile-container hcv-level-${level}`}>
        {paginatedItems.map((item, index) => {
          const isChecked =
            type === "course"
              ? checkedCourses[item.courseId]
              : type === "lesson"
                ? checkedLessons[item.lessonId]
                : type === "subject"
                  ? checkedSubjects[item.subjectId]
                  : true;
          const isLoading = checkboxLoading[item[type === "video" ? "_id" : `${type}Id`]] || false;
          if (type === "course") {
            const { shownVideos, totalVideos } = getCourseVideoCounts(item);
            return (
              <div key={item.courseId} className="hcv-tile hcv-course-tile">
                <div
                  className={`hcv-tile-header ${!isChecked ? "hcv-disabled" : ""}`}
                  onClick={isChecked ? () => toggleExpand("course", item.courseId) : undefined}
                >
                  <div className="hcv-title-container">
                    <span className="hcv-tile-title">
                      {index + 1}. {item.courseTitle}{" "}
                      <span className="hcv-video-count">({shownVideos}/{totalVideos} videos)</span>
                    </span>
                    {isChecked && (
                      <FontAwesomeIcon
                        icon={expandedCourses[item.courseId] ? faChevronUp : faChevronDown}
                        className="hcv-expand-icon"
                      />
                    )}
                  </div>
                  {isLoading ? (
                    <div className="hcv-checkbox-spinner">
                      <Spinner />
                    </div>
                  ) : (
                    <input
                      type="checkbox"
                      checked={checkedCourses[item.courseId] || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("course", item.courseId);
                      }}
                      className="hcv-checkbox"
                    />
                  )}
                </div>
                {expandedCourses[item.courseId] && (
                  <div className="hcv-tile-content">
                    {renderPaginatedItems(
                      item.lessons,
                      "lesson",
                      item.courseId,
                      level + 1,
                      paginationStates
                    )}
                  </div>
                )}
              </div>
            );
          } else if (type === "lesson") {
            const { shownVideos, totalVideos } = getLessonVideoCounts(item);
            return (
              <div key={item.lessonId} className="hcv-tile hcv-lesson-tile">
                <div
                  className={`hcv-tile-header ${!isChecked ? "hcv-disabled" : ""}`}
                  onClick={isChecked ? () => toggleExpand("lesson", item.lessonId) : undefined}
                >
                  <div className="hcv-title-container">
                    <span className="hcv-tile-title">
                      {item.lessonName}{" "}
                      <span className="hcv-video-count">({shownVideos}/{totalVideos} videos)</span>
                    </span>
                    {isChecked && (
                      <FontAwesomeIcon
                        icon={expandedLessons[item.lessonId] ? faChevronUp : faChevronDown}
                        className="hcv-expand-icon"
                      />
                    )}
                  </div>
                  {isLoading ? (
                    <div className="hcv-checkbox-spinner">
                      <Spinner />
                    </div>
                  ) : (
                    <input
                      type="checkbox"
                      checked={checkedLessons[item.lessonId] || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("lesson", item.lessonId);
                      }}
                      className="hcv-checkbox"
                    />
                  )}
                </div>
                {expandedLessons[item.lessonId] && (
                  <div className="hcv-tile-content">
                    {renderPaginatedItems(
                      item.subjects,
                      "subject",
                      item.lessonId,
                      level + 1,
                      paginationStates
                    )}
                  </div>
                )}
              </div>
            );
          } else if (type === "subject") {
            const { shownVideos, totalVideos } = getSubjectVideoCounts(item);
            return (
              <div key={item.subjectId} className="hcv-tile hcv-subject-tile">
                <div
                  className={`hcv-tile-header ${!isChecked ? "hcv-disabled" : ""}`}
                  onClick={isChecked ? () => toggleExpand("subject", item.subjectId) : undefined}
                >
                  <div className="hcv-title-container">
                    <span className="hcv-tile-title">
                      {item.subjectName}{" "}
                      <span className="hcv-video-count">({shownVideos}/{totalVideos} videos)</span>
                    </span>
                    {isChecked && (
                      <FontAwesomeIcon
                        icon={expandedSubjects[item.subjectId] ? faChevronUp : faChevronDown}
                        className="hcv-expand-icon"
                      />
                    )}
                  </div>
                  {isLoading ? (
                    <div className="hcv-checkbox-spinner">
                      <Spinner />
                    </div>
                  ) : (
                    <input
                      type="checkbox"
                      checked={checkedSubjects[item.subjectId] || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("subject", item.subjectId);
                      }}
                      className="hcv-checkbox"
                    />
                  )}
                </div>
                {expandedSubjects[item.subjectId] && (
                  <div className="hcv-tile-content">
                    {item.videos.length > 0 ? (
                      renderPaginatedItems(
                        item.videos,
                        "video",
                        item.subjectId,
                        level + 1,
                        paginationStates
                      )
                    ) : (
                      <div className="hcv-tile hcv-no-data-tile">No videos found</div>
                    )}
                  </div>
                )}
              </div>
            );
          } else if (type === "video") {
            return (
              <div key={item._id} className="hcv-tile hcv-video-tile">
                <div className="hcv-tile-header">
                  <div className="hcv-title-container">
                    <span className="hcv-tile-title">{item.name}</span>
                  </div>
                  {isLoading ? (
                    <div className="hcv-checkbox-spinner">
                      <Spinner />
                    </div>
                  ) : (
                    <input
                      type="checkbox"
                      checked={checkedVideos[item._id] || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCheckboxChange("video", item._id);
                      }}
                      className="hcv-checkbox"
                    />
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
        {totalPages > 1 && type !== "course" && (
          <div className="hcv-sub-pagination">
            <button
              onClick={() => handleSubPagination(type, parentId, "prev")}
              disabled={currentPageForType === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span>
              Page {currentPageForType} of {totalPages}
            </span>
            <button
              onClick={() => handleSubPagination(type, parentId, "next")}
              disabled={currentPageForType === totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Prepare pagination states
  const paginationStates = { lessonPages, subjectPages, videoPages };

  return (
    <div className="hcv-video-list-main">
      <img className="hcv-judicial-logo" src={judicialLogo} alt="Judicial Logo" />
      <h2 className="hcv-text-white">Select Courses</h2>

      <div className="hcv-controls-container">
        <div className="hcv-search-container">
          <FontAwesomeIcon icon={faSearch} className="hcv-search-icon" />
          <input
            type="text"
            placeholder="Search videos by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="hcv-search-input"
          />
        </div>

        <div className="hcv-dropdown-container">
          <select
            value={selectedCourse?.courseId || ""}
            onChange={(e) => handleDropdownChange("course", e.target.value)}
            className="hcv-dropdown"
          >
            <option value="">All Paid Courses</option>
            {getCoursesDetails
              .filter((course) => course.is_paid === true)
              .map((course) => (
                <option key={course.courseId} value={course.courseId}>
                  {course.courseTitle}
                </option>
              ))}
          </select>

          <select
            value={selectedLesson?.lessonId || ""}
            onChange={(e) => handleDropdownChange("lesson", e.target.value)}
            className="hcv-dropdown"
            disabled={!selectedCourse}
          >
            <option value="">All Lessons</option>
            {selectedCourse?.lessons.map((lesson) => (
              <option key={lesson.lessonId} value={lesson.lessonId}>
                {lesson.lessonName}
              </option>
            ))}
          </select>

          <select
            value={selectedSubject?.subjectId || ""}
            onChange={(e) => handleDropdownChange("subject", e.target.value)}
            className="hcv-dropdown"
            disabled={!selectedLesson}
          >
            <option value="">All Subjects</option>
            {selectedLesson?.subjects.map((subject) => (
              <option key={subject.subjectId} value={subject.subjectId}>
                {subject.subjectName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="hcv-tile-container">
        {loader ? (
          <div className="hcv-spinner-wrapper">
            <Spinner />
          </div>
        ) : filteredCourses.length > 0 ? (
          renderPaginatedItems(filteredCourses, "course", null, 1, paginationStates)
        ) : (
          <div className="hcv-tile hcv-no-data-tile">
            {searchQuery || selectedCourse
              ? "No paid videos found matching your criteria"
              : "No paid courses available"}
          </div>
        )}
      </div>

      <div className="hcv-save-container">
        <button className="hcv-cancel-button" onClick={handleCancel}>
          Back
        </button>
        <button
          className="hcv-save-button"
          onClick={handleSaveCourses}
          disabled={!student?._id || !hasChanges()}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default HideCourses;